import React, { useEffect, useState } from "react";
import { setCredentials, clearCredentials } from "./credentialsStore";

/**
 * Minimal React component that replicates the Vue SignatureGenerator functionality.
 * - Uses Web Crypto API to generate HMAC SHA256
 * - Validates JSON request body
 * - Generates timestamp
 * - Provides copy-to-clipboard for headers and cURL
 *
 * Drop this file into: /api-docs/src/components/SignatureGenerator.tsx
 * Use it in markdown as: <SignatureGenerator />
 */

function toHex(buffer: ArrayBuffer) {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

async function hmacSha256Hex(key: string, message: string) {
  // Guard for environments that don't expose the Web Crypto API (SSR / old browsers)
  const webCrypto = (globalThis as any).crypto;
  if (!webCrypto || !webCrypto.subtle) {
    throw new Error("Web Crypto API is not available in this environment.");
  }

  const enc = new TextEncoder();
  const keyData = enc.encode(key);
  const msgData = enc.encode(message);

  const cryptoKey = await webCrypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const sig = await webCrypto.subtle.sign("HMAC", cryptoKey, msgData);
  return toHex(sig);
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: 8,
  marginTop: 6,
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--background)",
  color: "var(--foreground)",
  fontFamily: "var(--font-sans)",
};

const buttonStyle: React.CSSProperties = {
  padding: "8px 14px",
  cursor: "pointer",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--background)",
  color: "var(--foreground)",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
};

const primaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: "var(--primary)",
  color: "var(--primary-foreground)",
  border: "1px solid var(--primary)",
  fontWeight: 600,
};

export default function SignatureGenerator() {
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [requestBody, setRequestBody] = useState("");
  const [timestamp, setTimestamp] = useState<string>(
    Math.floor(Date.now() / 1000).toString()
  );
  const [signature, setSignature] = useState("");
  const [messageToSign, setMessageToSign] = useState("");
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (apiKey && apiSecret) {
      setCredentials(apiKey, apiSecret);
      // Auto-select the ARP HMAC identity in Zudoku's playground (zustand persist format)
      sessionStorage.setItem(
        "identity-storage",
        JSON.stringify({
          state: { rememberedIdentity: "arp-hmac" },
          version: 0,
        })
      );
    }
  }, [apiKey, apiSecret]);

  const generateTimestamp = () => {
    setTimestamp(Math.floor(Date.now() / 1000).toString());
  };

  const generateSignature = async () => {
    setError("");
    setSignature("");
    setMessageToSign("");

    if (!apiKey || !apiSecret || !timestamp) {
      setError("API Key, API Secret and Timestamp are required.");
      return;
    }

    let bodyString = "";
    if (requestBody && requestBody.trim() !== "") {
      bodyString = requestBody.trim(); // Use exact string, don't canonicalize

      // Still validate it's valid JSON for user feedback
      try {
        JSON.parse(bodyString);
      } catch (e: any) {
        setError("Invalid JSON in request body. Please fix syntax.");
        return;
      }
    }

    const message = apiKey + bodyString + timestamp;
    setMessageToSign(message);

    try {
      const sig = await hmacSha256Hex(apiSecret, message);
      setSignature(sig);
    } catch (e: any) {
      console.error("HMAC error:", e);
      setError("Failed to generate signature: " + (e?.message || e));
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      const nav = (globalThis as any).navigator;
      if (!nav || !nav.clipboard) {
        console.warn("Clipboard API not available");
        return;
      }
      await nav.clipboard.writeText(text);
    } catch (e) {
      console.error("Clipboard error", e);
    }
  };

  const headersText = `X-API-Key: ${apiKey}
X-Timestamp: ${timestamp}
X-Signature: ${signature}`;

  const curlText = `curl -X POST "https://platform.arpdigital.io/api/v1/your-endpoint" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -H "X-Timestamp: ${timestamp}" \\
  -H "X-Signature: ${signature}"${requestBody ? ` \\\n  -d '${requestBody}'` : ""}`;

  // Avoid rendering interactive content during SSR to prevent runtime errors
  if (!mounted) {
    return <div />;
  }

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        padding: 18,
        borderRadius: "var(--radius)",
        width: "100%",
      }}
    >
      <h3>Interactive Signature Generator</h3>
      <p style={{ marginTop: 0, color: "var(--muted-foreground)" }}>
        Use this tool to generate and test HMAC SHA256 signatures for API
        requests.
      </p>

      <div style={{ marginBottom: 10 }}>
        <label>API Key</label>
        <input
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          placeholder="Enter your API key"
          disabled={false}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label>API Secret</label>
        <input
          value={apiSecret}
          onChange={e => setApiSecret(e.target.value)}
          placeholder="Enter your API secret"
          type="password"
          disabled={false}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label>Request Body (JSON)</label>
        <textarea
          value={requestBody}
          onChange={e => setRequestBody(e.target.value)}
          placeholder='{"example":"value"}'
          rows={4}
          style={inputStyle}
        />
        <small style={{ color: "var(--muted-foreground)" }}>Leave empty for GET requests</small>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label>Timestamp</label>
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <input
            value={timestamp}
            onChange={e => setTimestamp(e.target.value)}
            disabled={false}
            style={{
              ...inputStyle,
              flex: 1,
              marginTop: 0,
            }}
            placeholder="Unix timestamp (seconds)"
          />
          <button onClick={generateTimestamp} style={buttonStyle}>
            Generate Current
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button onClick={generateSignature} style={primaryButtonStyle}>
          Generate Signature
        </button>
        <button
          onClick={() => {
            setApiKey("");
            setApiSecret("");
            setRequestBody("");
            setSignature("");
            setMessageToSign("");
            setError("");
            setTimestamp(Math.floor(Date.now() / 1000).toString());
            clearCredentials();
          }}
          style={buttonStyle}
        >
          Reset
        </button>
      </div>

      {error && (
        <div
          style={{
            background: "color-mix(in oklab, var(--destructive) 10%, var(--background) 90%)",
            color: "var(--destructive)",
            padding: 10,
            borderRadius: "var(--radius)",
            marginBottom: 12,
            border: "1px solid color-mix(in oklab, var(--destructive) 20%, transparent)",
          }}
        >
          {error}
        </div>
      )}

      {signature && (
        <div style={{ marginTop: 12 }}>
          <h4>Generated Headers</h4>
          <pre
            style={{
              padding: 12,
              borderRadius: "var(--radius)",
              overflowX: "auto",
              border: "1px solid var(--border)",
            }}
          >
            {headersText}
          </pre>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button onClick={() => copyToClipboard(headersText)} style={buttonStyle}>
              Copy Headers
            </button>
            <button onClick={() => copyToClipboard(curlText)} style={buttonStyle}>
              Copy cURL
            </button>
          </div>

          <h4 style={{ marginTop: 14 }}>Message to Sign</h4>
          <pre
            style={{
              padding: 12,
              borderRadius: "var(--radius)",
              overflowX: "auto",
              border: "1px solid var(--border)",
            }}
          >
            {messageToSign}
          </pre>

          <h4 style={{ marginTop: 14 }}>cURL Example</h4>
          <pre
            style={{
              padding: 12,
              borderRadius: "var(--radius)",
              overflowX: "auto",
              border: "1px solid var(--border)",
            }}
          >
            {curlText}
          </pre>
        </div>
      )}
    </div>
  );
}
